import { BuyerUI } from '@choco-admin/types';
import { useCallback } from 'react';

import { useBuyersPageUpdateBuyerMutation } from '../../../generated';
import { mapToBuyerUI } from '../utils';

export type CaptureExceptionFunction = (error: Error) => void;

export type UpdateBuyerInput = {
  id: string;
  name?: string;
  displayName?: string;
  fullAddress?: string;
  addressCity?: string;
  addressCountry?: string;
  addressLatitude?: number;
  addressLongitude?: number;
  addressUTCOffset?: number;
  /**
   * `null` clears the stored id, `undefined` leaves it untouched. The
   * distinction is load-bearing: the backend shallow-merges the payload over the
   * stored buyer and the dynamo client turns an explicit `null` into a `REMOVE`,
   * so switching a buyer off a Google address must send `null` rather than
   * omitting the key. The validator allows it — `googlePlaceId` is
   * `Joi.optional().allow(null)` whenever `addressSource` is not `google`, and
   * `googlePlaceAddressId` allows `null` outright.
   */
  addressGooglePlaceId?: string | null;
  addressGoogleAddressId?: string | null;
  addressSource?: string;
  deliveryComment?: string;
  isTest?: boolean;
};

export type UseUpdateBuyerResponse = {
  updateBuyer: (input: UpdateBuyerInput) => Promise<BuyerUI>;
  isLoading: boolean;
};

export type UseUpdateBuyerConfig = {
  captureException?: CaptureExceptionFunction;
};

export const useUpdateBuyer = (
  config: UseUpdateBuyerConfig
): UseUpdateBuyerResponse => {
  const { captureException } = config;

  const [updateBuyerMutation, { loading }] = useBuyersPageUpdateBuyerMutation();

  const updateBuyer = useCallback(
    async (input: UpdateBuyerInput): Promise<BuyerUI> => {
      try {
        const result = await updateBuyerMutation({
          // Spelled out even though it is the client-wide default: the failure
          // handling below depends on this mutation RESOLVING with `errors`
          // rather than rejecting.
          errorPolicy: 'all',
          variables: {
            buyerId: input.id,
            buyerInput: {
              name: input.name,
              displayName: input.displayName,
              fullAddress: input.fullAddress,
              city: input.addressCity,
              country: input.addressCountry,
              latitude: input.addressLatitude,
              longitude: input.addressLongitude,
              utcOffset: input.addressUTCOffset,
              googlePlaceId: input.addressGooglePlaceId,
              googlePlaceAddressId: input.addressGoogleAddressId,
              addressSource: (() => {
                if (input.addressSource === 'google') return 'google';
                if (input.addressSource) return 'custom';
                return undefined;
              })(),
              deliveryComment: input.deliveryComment,
              isTest: input.isTest,
            },
          },
          refetchQueries: [
            'BuyersPageSearchBuyers', // Refetch buyers list
            'BuyersPageGetBuyerDetails', // Refetch buyer details
          ],
          awaitRefetchQueries: true,
        });

        const updatedBuyer = result.data?.buyerUpdate;

        // The payload decides, not `result.errors`. With `errorPolicy: 'all'` a
        // response can carry both an updated buyer and errors about fields the
        // server could not resolve; that write committed and the caller must get
        // the buyer. Only a missing payload is a failure — and because the
        // mutation resolved, the reason is in `result.errors`, never in a
        // rejection. Passing it on is what lets the caller tell a taken Google
        // place (409) from a Google lookup that failed (500).
        if (!updatedBuyer) {
          const reason = result.errors
            ?.map((graphQLError) => graphQLError.message)
            .join('; ');
          throw new Error(reason || 'Buyer update returned no buyer');
        }

        // Map to generic BuyerUI type using shared mapper
        return mapToBuyerUI(updatedBuyer);
      } catch (error) {
        const failure =
          error instanceof Error ? error : new Error('Buyer update failed');
        captureException?.(failure);
        throw failure;
      }
    },
    [updateBuyerMutation, captureException]
  );

  return {
    updateBuyer,
    isLoading: loading,
  };
};
